// OrderHistoryPage.jsx
import React, { useEffect, useState } from "react";
import { Container, Box, Grid, Typography } from "@mui/material";
import AccountSidebarNav from "../../Components/AccountComponents/AccountSideNacv";
import EmptyState from "../../Components/AccountComponents/EmptyState";
import { useDispatch, useSelector } from "react-redux";
import { getUserOrders } from "../../State/Order/Action";
import OrderCart from "../../Components/Order/OrderCart";
import FilterListIcon from "@mui/icons-material/FilterList";
import Loader from "../../Components/Loader/Loader";


// Step 1: Filter Labels
const orderStatusOptions = [
    { label: "On The Way", value: "ON_THE_WAY" },
    { label: "Delivered", value: "DELIVERED" },
    { label: "Cancelled", value: "CANCELLED" },
    { label: "Returned", value: "RETURNED" },
];

// Step 2: Backend to Frontend Status Map
const statusMap = {
    SHIPPED: "ON_THE_WAY",
    DELIVERED: "DELIVERED",
    CANCELLED: "CANCELLED",
    RETURNED: "RETURNED",
};


export default function OrderHistory() {
    const dispatch = useDispatch();
    const { order } = useSelector((store) => store);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [showFilter, setShowFilter] = useState(false);

    useEffect(() => {
        dispatch(getUserOrders());
    }, [dispatch]);

    // Handle checkbox change
    const handleStatusChange = (statusValue) => {
        setSelectedStatuses((prev) =>
            prev.includes(statusValue)
                ? prev.filter((val) => val !== statusValue)
                : [...prev, statusValue]
        );
    };

    const renderFilters = () => (
        <div className="shadow-lg bg-white p-5 rounded-md w-full">
            <h1 className="font-bold text-lg">Filters</h1>
            <div className="space-y-4 mt-6">
                <h2 className="font-semibold text-sm text-gray-700">ORDER STATUS</h2>
                {orderStatusOptions.map((option) => (
                    <div className="flex items-center" key={option.value}>
                        <input
                            type="checkbox"
                            value={option.value}
                            id={option.value}
                            className="h-4 w-4 border-gray-300 text-red-600 focus:ring-red-500"
                            checked={selectedStatuses.includes(option.value)}
                            onChange={() => handleStatusChange(option.value)}
                        />
                        <label htmlFor={option.value} className="ml-3 text-sm text-gray-600">
                            {option.label}
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );

    // Filter orders based on selected statuses
    const filteredOrders = order?.orders?.response?.filter((item) => {
        const mappedStatus = statusMap[item?.orderStatus?.toUpperCase()];
        return selectedStatuses?.length === 0 || selectedStatuses.includes(mappedStatus);
    });

    return (
        <Container maxWidth={false} sx={{
            py: 2,
            // Responsive maxWidth for different screen sizes
            maxWidth: {
                xs: '100%',  // Mobile
                sm: '95%',   // Small tablets
                md: '92%',   // Tablets and small laptops
                lg: '90%',   // Large screens
                xl: '90%',   // Very large displays
            },
            mx: 'auto', // Center align container
        }}>
            {/* Breadcrumb */}
            <Box
                sx={{
                    mb: 2,
                    pb: 2,
                    borderBottom: "1px solid #e5e5e5",
                }}
            >
                <Typography
                    sx={{
                        textTransform: "uppercase",
                        fontSize: "12px",
                        letterSpacing: "0.05em",
                        color: "text.secondary",
                    }}
                >
                    <span style={{ color: "#000" }}>My Account</span>
                    <span style={{ margin: "0 8px" }}>{">"}</span>
                    <span style={{ color: "#000" }}>My Orders</span>
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {/* Sidebar */}
                <Grid item xs={12} md={3}>
                    <AccountSidebarNav active="Order History" />
                </Grid>

                <Grid item xs={12} md={9}>
                    {/* ✅ Case 1: No orders at all */}
                    {order?.orders?.response?.length === 0 ? (
                        <EmptyState
                            imageSrc="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/clothes-hangers-on-rack-jbK3YLkiXvW2FLK3mgz6dxhojnf4Nb.jpg"
                            imageAlt="Empty closet"
                            title="Your closet is empty!"
                            subtitle="Let's do some shopping."
                            buttonText="Start Shopping"
                            buttonHref="/"
                        />
                    ) : order?.loading ? <Loader /> : (
                        <>
                            {/* ✅ Header Row → Count + Filter Toggle */}
                            <div className="flex items-center justify-between mb-3">
                                <Typography
                                    sx={{
                                        textTransform: "uppercase",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        letterSpacing: "0.05em",
                                        color: "text.primary",
                                    }}
                                >
                                    My Orders{" "}
                                    <span style={{ fontWeight: 400, color: "#777" }}>
                                        ({filteredOrders?.length} items)
                                    </span>
                                </Typography>

                                <button
                                    onClick={() => setShowFilter((prev) => !prev)}
                                    className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-500"
                                >
                                    <FilterListIcon className="mr-2" />{" "}
                                    {showFilter ? "Hide Filters" : "Show Filters"}
                                </button>
                            </div>

                            {/* ✅ Filters */}
                            {showFilter && <div className="sticky top-5">{renderFilters()}</div>}

                            {/* ✅ Case 2: orders > 0 but filteredOrders is empty */}
                            {filteredOrders?.length > 0 ? (
                                /* ✅ Case 3: filteredOrders has items */
                                <div className="space-y-5">
                                    {filteredOrders?.map((item) => (
                                        <OrderCart key={item.id} item={item} />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState
                                    imageSrc="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/clothes-hangers-on-rack-jbK3YLkiXvW2FLK3mgz6dxhojnf4Nb.jpg"
                                    imageAlt="Empty closet"
                                    title="No results found!"
                                    subtitle="Try adjusting your filters."
                                    buttonText="Clear Filters"
                                    buttonHref="#"
                                />
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        </Container>
    );
}
